import { useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, api } from '../api/client';
import type { components } from '../api/schema.gen';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { Modal } from './Modal';
import { PageLoader } from './PageLoader';

type Member = components['schemas']['Member'];

/**
 * The "ask an admin/owner" modal, shared between Dashboard (LLMR-55, reason
 * 'balance') and Playground (LLMR-70, reason 'quota') — same
 * POST /balance/request-topup underneath, same shared per-org cooldown
 * regardless of reason, same send-all-or-pick-one UI. Only `title`/`cta` and
 * the `reason` sent to the server differ per caller.
 */
export function RequestTopUpModal({
  reason,
  title,
  cta,
  onClose,
  onResult,
}: {
  reason: 'balance' | 'quota';
  title: string;
  cta: string;
  onClose: () => void;
  onResult: (result: 'sent' | 'cooldown' | 'error') => void;
}) {
  const { dict } = useLanguage();
  const [sendMode, setSendMode] = useState<'all' | 'specific'>('all');
  // Roster is fetched lazily, only once "specific" is picked — most requests
  // never need it.
  const [recipients, setRecipients] = useState<Member[] | null>(null);
  const [recipientsError, setRecipientsError] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState('');

  async function loadRecipients() {
    if (recipients !== null) return;
    try {
      const res = await api.team({ page_size: 50, sort: 'role' });
      setRecipients(res.data.filter((m) => m.role === 'owner' || m.role === 'admin'));
    } catch {
      setRecipientsError(true);
    }
  }

  async function submit(formEvent: FormEvent) {
    formEvent.preventDefault();
    onClose();
    try {
      await api.requestTopUp(sendMode === 'specific' ? selectedRecipient : undefined, reason);
      onResult('sent');
    } catch (e) {
      onResult(e instanceof ApiError && e.status === 429 ? 'cooldown' : 'error');
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <div className="request-topup-modes" role="radiogroup" aria-label={title}>
          <label className={`chip${sendMode === 'all' ? ' chip--active' : ''}`}>
            <input
              type="radio"
              className="visually-hidden"
              name="topup-send-mode"
              checked={sendMode === 'all'}
              onChange={() => setSendMode('all')}
            />
            {dict.dashboard.requestTopUpSendAll}
          </label>
          <label className={`chip${sendMode === 'specific' ? ' chip--active' : ''}`}>
            <input
              type="radio"
              className="visually-hidden"
              name="topup-send-mode"
              checked={sendMode === 'specific'}
              onChange={() => {
                setSendMode('specific');
                void loadRecipients();
              }}
            />
            {dict.dashboard.requestTopUpSendSpecific}
          </label>
        </div>

        {sendMode === 'specific' && (
          <div className="request-topup-recipient">
            {recipients === null && !recipientsError && <PageLoader compact />}
            {recipientsError && <p className="note warn-text">{dict.dashboard.requestTopUpError}</p>}
            {recipients !== null && (
              <select
                aria-label={dict.dashboard.requestTopUpPickPlaceholder}
                value={selectedRecipient}
                onChange={(e) => setSelectedRecipient(e.target.value)}
                required
              >
                <option value="" disabled>
                  {dict.dashboard.requestTopUpPickPlaceholder}
                </option>
                {recipients.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name || m.email} ({m.role})
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={sendMode === 'specific' && !selectedRecipient}>
            {cta}
          </button>
        </div>
      </form>
    </Modal>
  );
}
